import BrandTagline from '@/components/site/BrandTagline';
import MonashConnectionResearch from "@/components/site/MonashConnectionResearch";
import { Link } from "react-router-dom";
import { ShieldCheck, EyeOff, Ban, ArrowRight, Radar, UserCheck, Users, MapPin, Handshake } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import ComingSoonButtons from "@/components/site/ComingSoonButtons";
import Photo from "@/components/site/Photo";
import SectionIntro from "@/components/site/SectionIntro";
import {ProductShot, VibesGrid} from "@/components/site/PeopleProduct";
import useSeo from "@/hooks/useSeo";

const howSteps = [
  { icon: Radar, title: "Discover", body: "Open the Radar and see people and events nearby." },
  { icon: UserCheck, title: "Connect by choice", body: "Send a request. Connections happen only when both people say yes." },
  { icon: Users, title: "Meet in person", body: "Once you both accept, Orrbbit unlocks the meetup experience so you can find each other." },
];

const safetyPoints = [
  { icon: Handshake, text: "Consent-based connections" },
  { icon: EyeOff, text: "Privacy-controlled Radar" },
  { icon: Ban, text: "Block & report" },
  { icon: ShieldCheck, text: "Verification tools" },
];

export default function Home() {
  useSeo({
    title: "Orrbbit — Real People. Real Moments. Right Nearby.",
    description: "Meet people nearby based on your vibe — make new friends, meet for coffee or drinks, find an activity buddy, network locally or find something real. All while staying in control of your privacy.",
    path: "/",
  });

  return (
    <div data-testid="home-page">
      {/* 1 · HERO */}
      <section className="relative overflow-hidden bg-lightbg">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-teal/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-orange/10 blur-3xl" />
        <div className="container-x relative grid grid-cols-1 items-center gap-8 py-10 md:py-14 lg:grid-cols-2">
          <Reveal>
            <BrandTagline compact/>
            <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl">
              Discover <span className="text-teal brand-people">people</span> and <span className="text-orange">events</span> around you.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-bodygrey">
              Orrbbit helps you turn nearby opportunities into real-world connections — on your terms.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/get-started"
                data-testid="hero-discover-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 font-semibold text-white shadow-soft transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2"
              >
                Get Started <ArrowRight size={18} />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-how-btn"
                className="inline-flex items-center gap-2 rounded-full border-2 border-teal bg-white px-7 py-3.5 font-semibold text-teal transition-colors hover:bg-teal hover:text-white"
              >
                See how it works
              </Link>
            </div>
            <div className="mt-8">
              <ComingSoonButtons />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative" data-testid="home-hero-photo">
              <Photo slug="hero-connection" ratio="aspect-[5/4]" priority rounded="rounded-[2rem]" className="shadow-card" />
              <div className="absolute left-5 top-5 -rotate-3 rounded-2xl bg-white/90 px-4 py-2.5 shadow-soft backdrop-blur md:left-7 md:top-7" data-testid="home-hero-note">
                <p className="font-hand text-2xl leading-[0.95] text-navy md:text-4xl">Life happens</p>
                <p className="font-hand text-2xl leading-[0.95] text-navy md:text-4xl">better together</p>
                <svg viewBox="0 0 120 12" className="mt-1 h-2.5 w-24 text-teal md:w-28" fill="none" aria-hidden="true">
                  <path d="M3 9 C 30 2, 60 2, 117 7" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                </svg>
              </div>
              <div className="absolute bottom-5 right-5 flex rotate-2 items-center gap-2.5 rounded-2xl bg-white/95 px-4 py-3 shadow-card backdrop-blur md:bottom-7 md:right-7" data-testid="home-hero-card">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-teal/10 text-teal"><MapPin size={17} /></span>
                <span>
                  <span className="block text-xs font-semibold text-navy">People &amp; events</span>
                  <span className="block text-[11px] text-bodygrey">right nearby</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-x section-space"><SectionIntro center eyebrow="Your local world" title="Discover. Connect. Meet." body="People and Events nearby, with mutual choice at every step."/><div className="mt-10 grid items-start gap-8 md:grid-cols-3"><ProductShot kind="radar"/><ProductShot kind="discovery"/><ProductShot kind="profile"/></div></section>

      {/* 4 · HOW ORRBITT WORKS — simplified */}
      <section className="border-t border-border bg-white" data-testid="home-how-it-works">
        <div className="container-x py-10 md:py-14">
          <SectionIntro
            center
            eyebrow="How it works"
            title="From nearby to face-to-face"
            body="The app helps you find each other — the important part happens in person."
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-how-steps">
            {howSteps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-border bg-lightbg p-6 shadow-soft" data-testid={`home-how-step-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 text-teal"><s.icon size={22} /></span>
                  <div className="mt-4 text-xs font-semibold text-orange">STEP {i + 1}</div>
                  <h3 className="mt-1 font-heading text-lg font-semibold text-navy">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-bodygrey">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 text-center">
            <Link to="/how-it-works" data-testid="home-how-cta" className="inline-flex items-center gap-1 font-semibold text-teal hover:underline">
              See how Orrbbit works <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border bg-lightbg" data-testid="home-trust-section">
        <div className="container-x py-8 md:py-10">
          <h2 className="font-heading text-2xl font-semibold text-navy">Your choice. Your privacy.</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {safetyPoints.map(({icon: Icon, text}) => <li key={text} className="flex items-center gap-2 text-sm text-navy"><Icon size={18} className="shrink-0 text-teal" aria-hidden="true" />{text}</li>)}
          </ul>
          <Link to="/safety" className="mt-5 inline-flex items-center gap-1 font-semibold text-teal hover:underline">Visit the Safety Centre <ArrowRight size={16} /></Link>
        </div>
      </section>

      <MonashConnectionResearch showBenefitsLink />

      <section className="container-x section-space"><SectionIntro center eyebrow="Every Vibe unlocked" title="What are you open to today?" body="Orrbbit People is completely free, with every active Vibe and Radar up to 1km."/><VibesGrid compact/></section>

    </div>
  );
}
